import { useState, useRef, useEffect, type RefObject } from "react";
import type { ScannedBarcode } from "../types/BarcodeDetection";

const useHandheldBarcode = (
  currentBarcode: RefObject<ScannedBarcode[]>,
  scannedHandler: () => void
) => {
  const [scannedCode, setScannedCode] = useState<string>("");
  const [showManualInput, setShowManualInput] = useState<boolean>(false);
  const [manualCode, setManualCode] = useState<string>("");

  // Keep the session stats state (for future reference if needed)

  // Refs for barcode scanning with proper types
  const barcodeBuffer = useRef<string>("");
  const barcodeTimeout = useRef<number | null>(null);
  const preventListener = useRef<boolean>(false);

  // Handle barcode scanner input
  useEffect(() => {
    // Barcode scanner typically sends input very fast followed by an Enter key
    const handleKeyDown = (e: KeyboardEvent): void => {
      // If the manual input modal is open, don't capture keyboard input
      if (showManualInput) return;

      if (preventListener.current) {
        return; // Skip processing if preventListener is set
      }

      // If Enter key is pressed, process the accumulated buffer as a barcode
      if (e.key === "Enter" && barcodeBuffer.current) {
        e.preventDefault();
        const code = barcodeBuffer.current;
        barcodeBuffer.current = ""; // Clear buffer
        handleCodeScan(code);
        return;
      }

      // Add character to buffer
      if (e.key.length === 1) {
        barcodeBuffer.current += e.key;

        // Reset the timeout each time a new character is added
        if (barcodeTimeout.current) {
          window.clearTimeout(barcodeTimeout.current);
        }

        // Set a timeout to clear the buffer if no more input is received
        // This distinguishes between manual typing and barcode scanner
        barcodeTimeout.current = window.setTimeout(() => {
          // If the buffer still has content after timeout and no Enter was pressed,
          // it might still be a barcode (some scanners don't send Enter)
          if (barcodeBuffer.current.length >= 4) {
            // Assume codes are at least 4 chars
            const code = barcodeBuffer.current;
            barcodeBuffer.current = "";
            handleCodeScan(code);
          } else {
            barcodeBuffer.current = ""; // Clear buffer if it's likely not a barcode
          }
        }, 100); // Short timeout as barcode scanners send characters rapidly
      }
    };

    // Add the event listener
    document.addEventListener("keydown", handleKeyDown);

    // Clean up
    return (): void => {
      document.removeEventListener("keydown", handleKeyDown);
      if (barcodeTimeout.current) {
        window.clearTimeout(barcodeTimeout.current);
      }
    };
  }, [showManualInput, preventListener]); // Re-attach listener if showManualInput changes

  // Process scanned code - no whitelist validation
  const handleCodeScan = (
    code: string,
    isManualOverride: boolean = false
  ): void => {
    setScannedCode(code);
    // All scans are now considered valid since there's no whitelist

    // assume it will always index 3 (4 code)
    currentBarcode.current[3] = {
      barcode: code,
      timestamp: new Date().toISOString(),
      isManualOverride: isManualOverride,
    };

    scannedHandler();
  };

  // Handle manual code submission
  const handleManualSubmit = (): void => {
    if (manualCode.trim()) {
      handleCodeScan(manualCode.trim(), true); // Set manual override flag to true
      setManualCode("");
      setShowManualInput(false);
    }
  };

  return {
    scannedCode,
    setScannedCode,
    showManualInput,
    setShowManualInput,
    manualCode,
    setManualCode,
    handleManualSubmit,
    handleCodeScan,
    preventListener,
  };
};

export default useHandheldBarcode;
